import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogApi } from '../api';
import { CatalogJsonEditor, PublishCatalogDialog, SectionFormDialog, WarningBadge, WarningPanel, AuditLogPanel, TokensTab } from '../components/catalog';
import { TemplatesTab } from '../components/catalog/templates/TemplatesTab';
import { ThemesTab } from '../components/catalog/themes/ThemesTab';
import { useWarnings } from '../hooks/useWarnings';
import {
  ChevronRight,
  Calendar,
  User,
  CheckCircle,
  Clock,
  AlertTriangle,
  Loader2,
  ArrowLeft,
  FileCheck,
  Download,
  Plus,
  Edit,
  Copy,
  Trash2,
  Search,
  X,
  Eye,
  Layers,
  Info,
} from 'lucide-react';
import clsx from 'clsx';
import type { CatalogContent, CatalogSection } from '../api';
import { usePublicSettings } from '../hooks/usePublicSettings';

export function CatalogEditorPage() {
  const { version } = useParams<{ version: string }>();
  const queryClient = useQueryClient();
  const { get: getSetting } = usePublicSettings();
  const [activeTab, setActiveTab] = useState<'json' | 'sections' | 'tokens' | 'templates' | 'themes'>('json');
  const [showExportSuccess, setShowExportSuccess] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showSectionDialog, setShowSectionDialog] = useState(false);
  const [editingSection, setEditingSection] = useState<{ id: string; data: CatalogSection } | undefined>();
  const [deletingSection, setDeletingSection] = useState<string | null>(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSlot, setFilterSlot] = useState<string>('');

  // Warning Panel state
  const [showWarningPanel, setShowWarningPanel] = useState(false);
  const [dismissedWarnings, setDismissedWarnings] = useState<string[]>(() => {
    // Load dismissed warnings from localStorage
    try {
      const stored = localStorage.getItem(`catalog-warnings-dismissed-v${version}`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Audit Log Panel state
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Fetch catalog version
  const { data, isLoading, error } = useQuery({
    queryKey: ['catalog', 'version', version],
    queryFn: () => catalogApi.getVersion(Number(version!)),
    enabled: !!version,
  });

  const catalog = data?.data.data;
  const isDraft = catalog?.status === 'draft';
  const isPublished = catalog?.status === 'published';

  // Fetch audit logs (only when panel is open)
  const { data: auditLogsData, isLoading: isLoadingAuditLogs } = useQuery({
    queryKey: ['catalog', 'audit-log', version],
    queryFn: () => catalogApi.getAuditLog(Number(version!)),
    enabled: !!version && showAuditLog,
  });

  // Prepare data for warnings hook
  const sections = catalog?.catalog.sections
    ? Object.entries(catalog.catalog.sections).map(([id, data]) => ({ id, data }))
    : [];
  const availableSlots = catalog?.catalog.slots
    ? Object.keys(catalog.catalog.slots)
    : [];
  const templates = catalog?.catalog.templates || {};

  // Warning System (with cross-entity validation)
  const { warnings, hasWarnings } = useWarnings({
    sections,
    availableSlots,
    templates: templates as any,
    dismissedWarnings,
  });

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+N / Cmd+N: New section (only in sections tab and draft mode)
      if ((e.ctrlKey || e.metaKey) && e.key === 'n') {
        if (activeTab === 'sections' && isDraft && !showSectionDialog) {
          e.preventDefault();
          handleCreateSection();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, isDraft, showSectionDialog]);

  // Update catalog mutation
  const updateMutation = useMutation({
    mutationFn: async (content: CatalogContent) => {
      return catalogApi.update(Number(version!), { catalog: content });
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['catalog', 'version', version] });
      queryClient.invalidateQueries({ queryKey: ['catalog', 'versions'] });
    },
  });

  const handleSave = async (content: CatalogContent) => {
    try {
      await updateMutation.mutateAsync(content);
    } catch (error) {
      console.error('Failed to save catalog:', error);
      throw error; // Re-throw to let CatalogJsonEditor handle it
    }
  };

  const handleSaveSection = async (sectionId: string, sectionData: CatalogSection) => {
    if (!catalog) return;

    try {
      // Update the sections in catalog
      const updatedCatalog: CatalogContent = {
        ...catalog.catalog,
        sections: {
          ...catalog.catalog.sections,
          [sectionId]: sectionData,
        },
      };

      await updateMutation.mutateAsync(updatedCatalog);
      setShowSectionDialog(false);
      setEditingSection(undefined);

      // Show success toast
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save section:', error);
      throw error; // Re-throw to let SectionFormDialog handle it
    }
  };

  const handleCreateSection = () => {
    setEditingSection(undefined);
    setShowSectionDialog(true);
  };

  const handleSaveTokens = async (updatedDefaults: Record<string, string | number>) => {
    if (!catalog) return;

    try {
      // Update designTokens.defaults in catalog
      const existingTokens = catalog.catalog.designTokens;
      const updatedCatalog: CatalogContent = {
        ...catalog.catalog,
        designTokens: {
          ...existingTokens,
          defaults: updatedDefaults as any,
          definitions: existingTokens?.definitions ?? {},
        },
      };

      await updateMutation.mutateAsync(updatedCatalog);

      // Show success toast
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save tokens:', error);
      throw error; // Re-throw to let TokensTab handle it
    }
  };

  const handleSaveTemplates = async (updatedTemplates: Record<string, any>) => {
    if (!catalog) return;

    try {
      // Update templates in catalog
      const updatedCatalog: CatalogContent = {
        ...catalog.catalog,
        templates: updatedTemplates as any,
      };

      await updateMutation.mutateAsync(updatedCatalog);

      // Show success toast
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save templates:', error);
      throw error; // Re-throw to let TemplatesTab handle it
    }
  };

  const handleSaveThemes = async (updatedThemes: any[]) => {
    if (!catalog) return;

    try {
      // Update themes in catalog
      const updatedCatalog: CatalogContent = {
        ...catalog.catalog,
        themes: updatedThemes as any,
      };

      await updateMutation.mutateAsync(updatedCatalog);

      // Show success toast
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save themes:', error);
      throw error; // Re-throw to let ThemesTab handle it
    }
  };

  const handleEditSection = (sectionId: string) => {
    if (!catalog) return;
    const sectionData = catalog.catalog.sections[sectionId];
    if (!sectionData) return;

    setEditingSection({ id: sectionId, data: sectionData });
    setShowSectionDialog(true);
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!catalog) return;

    try {
      // Remove section from catalog
      const { [sectionId]: removed, ...remainingSections } = catalog.catalog.sections;

      const updatedCatalog: CatalogContent = {
        ...catalog.catalog,
        sections: remainingSections,
      };

      await updateMutation.mutateAsync(updatedCatalog);
      setDeletingSection(null);
    } catch (error) {
      console.error('Failed to delete section:', error);
      // Error will be shown by mutation error handling
    }
  };

  const handleDuplicateSection = async (sectionId: string) => {
    if (!catalog) return;
    const sectionData = catalog.catalog.sections[sectionId];
    if (!sectionData) return;

    try {
      // Generate new ID
      let newId = `${sectionId}-copy`;
      let counter = 1;
      while (catalog.catalog.sections[newId]) {
        newId = `${sectionId}-copy-${counter}`;
        counter++;
      }

      // Create duplicated section
      const updatedCatalog: CatalogContent = {
        ...catalog.catalog,
        sections: {
          ...catalog.catalog.sections,
          [newId]: {
            ...sectionData,
            label: sectionData.label ? `${sectionData.label} (Copy)` : undefined,
          },
        },
      };

      await updateMutation.mutateAsync(updatedCatalog);
    } catch (error) {
      console.error('Failed to duplicate section:', error);
    }
  };

  const handlePublish = async () => {
    const confirmFirst = getSetting<boolean>('catalog.confirmBeforePublish');
    if (confirmFirst) {
      setShowPublishDialog(true);
    } else {
      // Publish directly without confirmation dialog
      try {
        await catalogApi.publish(Number(version));
        queryClient.invalidateQueries({ queryKey: ['catalog'] });
      } catch (err) {
        console.error('Failed to publish:', err);
      }
    }
  };

  const handlePreview = async () => {
    if (!version) return;

    setIsGeneratingPreview(true);
    try {
      const response = await catalogApi.createPreviewToken(Number(version));

      if (response.data?.data) {
        const { previewUrl, expiresInSeconds } = response.data.data;

        // Open preview in new tab
        window.open(previewUrl, '_blank');

        // Show success message (could be replaced with toast)
        const expiresMin = Math.floor(expiresInSeconds / 60);
        alert(`Preview opened! Expires in ${expiresMin} minutes.`);
      }
    } catch (error: any) {
      console.error('Preview failed:', error);
      const message = error.response?.data?.message || 'Failed to generate preview. Check permissions.';
      alert(message);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleExport = () => {
    if (!catalog) return;

    try {
      // Format JSON with indent 2
      const jsonString = JSON.stringify(catalog.catalog, null, 2);

      // Create blob
      const blob = new Blob([jsonString], { type: 'application/json' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `product-catalog-v${catalog.version}.json`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success toast
      setShowExportSuccess(true);
      setTimeout(() => setShowExportSuccess(false), 3000);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handleDismissWarning = (warningId: string) => {
    const updated = [...dismissedWarnings, warningId];
    setDismissedWarnings(updated);

    // Persist to localStorage
    try {
      localStorage.setItem(`catalog-warnings-dismissed-v${version}`, JSON.stringify(updated));
    } catch (error) {
      console.error('Failed to save dismissed warnings:', error);
    }
  };

  const handleNavigateToSection = (sectionId: string) => {
    // Scroll to section card
    const sectionElement = document.getElementById(`section-${sectionId}`);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Highlight the section briefly
      sectionElement.classList.add('ring-2', 'ring-primary-500', 'ring-offset-2');
      setTimeout(() => {
        sectionElement.classList.remove('ring-2', 'ring-primary-500', 'ring-offset-2');
      }, 2000);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Error state
  if (error || !catalog) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-amber-400 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">
          Versione catalogo non trovata
        </h2>
        <p className="text-gray-600 mb-4">
          La versione del catalogo {version} richiesta non può essere caricata.
        </p>
        <Link to="/catalog/versions" className="btn-primary">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Torna alle Versioni
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center text-sm text-gray-500">
          <Link to="/catalog" className="hover:text-gray-700">
            Catalog
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link to="/catalog/versions" className="hover:text-gray-700">
            Versions
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">v{catalog.version}</span>
        </div>
      </div>

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-2xl font-bold text-gray-900">
                Catalog Version {catalog.version}
              </h1>
              <span className={clsx(
                'px-3 py-1 rounded-full text-sm font-medium',
                isPublished
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              )}>
                {isPublished ? (
                  <><CheckCircle className="w-3 h-3 inline mr-1" />Pubblicata</>
                ) : (
                  <><Clock className="w-3 h-3 inline mr-1" />Bozza</>
                )}
              </span>
              {!isDraft && (
                <span className="text-sm text-gray-500 flex items-center">
                  <FileCheck className="w-4 h-4 mr-1" />
                  Sola lettura
                </span>
              )}
            </div>

            {/* Metadata */}
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              {catalog.createdBy && (
                <div className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  <span>Creata da {catalog.createdBy.name}</span>
                </div>
              )}
              {catalog.createdAt && (
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  <span>{new Date(catalog.createdAt).toLocaleDateString('it-IT')}</span>
                </div>
              )}
              {catalog.publishedAt && (
                <div className="flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1 text-green-600" />
                  <span>Pubblicata il {new Date(catalog.publishedAt).toLocaleDateString('it-IT')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            <Link to="/catalog/versions" className="btn-secondary">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla Lista
            </Link>
            <button
              onClick={handleExport}
              className="btn-secondary"
              title="Scarica file JSON"
            >
              <Download className="w-4 h-4 mr-2" />
              Scarica JSON
            </button>
            <button
              onClick={() => setShowAuditLog(true)}
              className="btn-secondary"
              title="Visualizza cronologia modifiche"
            >
              <Clock className="w-4 h-4 mr-2" />
              Cronologia
            </button>
            {isDraft && (
              <>
                <button
                  onClick={handlePreview}
                  disabled={isGeneratingPreview}
                  className="btn-secondary flex items-center"
                  title="Preview draft catalog on site"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {isGeneratingPreview ? 'Generating...' : 'Preview'}
                </button>
                <button
                  onClick={handlePublish}
                  className="btn-primary"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Pubblica
                </button>
              </>
            )}
          </div>
        </div>

        {/* Warning for published version */}
        {isPublished && (
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start space-x-2 text-sm text-blue-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Questa è una versione pubblicata</p>
                <p className="mt-1">
                  Le versioni pubblicate sono di sola lettura. Crea una nuova versione o ripubblica
                  una bozza se devi apportare modifiche.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('json')}
            className={clsx(
              'py-3 px-1 border-b-2 text-sm font-medium transition-colors',
              activeTab === 'json'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            Editor JSON
          </button>
          <button
            onClick={() => setActiveTab('sections')}
            className={clsx(
              'py-3 px-1 border-b-2 text-sm font-medium transition-colors',
              activeTab === 'sections'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
            disabled={!isDraft}
          >
            Sezioni
          </button>
          <button
            onClick={() => setActiveTab('tokens')}
            className={clsx(
              'py-3 px-1 border-b-2 text-sm font-medium transition-colors',
              activeTab === 'tokens'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            Tokens
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={clsx(
              'py-3 px-1 border-b-2 text-sm font-medium transition-colors',
              activeTab === 'templates'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab('themes')}
            className={clsx(
              'py-3 px-1 border-b-2 text-sm font-medium transition-colors',
              activeTab === 'themes'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            )}
          >
            Themes
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden bg-gray-50">
        {activeTab === 'json' && (
          <CatalogJsonEditor
            initialValue={catalog.catalog}
            onSave={handleSave}
            readOnly={!isDraft}
            className="h-full"
          />
        )}
        {activeTab === 'sections' && (
          <div className="h-full flex flex-col">
            {/* Fixed Header */}
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-200 bg-white sticky top-0 z-10">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Sezioni</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      Gestisci le sezioni del catalogo ({Object.keys(catalog.catalog.sections || {}).length} sezioni)
                    </p>
                  </div>
                  {hasWarnings && (
                    <WarningBadge
                      warnings={warnings}
                      onClick={() => setShowWarningPanel(true)}
                    />
                  )}
                </div>
                {isDraft && (
                  <button
                    onClick={handleCreateSection}
                    className="btn-primary flex items-center space-x-2"
                    title="Nuova Sezione (Ctrl+N)"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Crea Sezione</span>
                  </button>
                )}
              </div>

              {/* Info Banner */}
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm text-blue-800">
                  <p>
                    Le sezioni sono componenti riutilizzabili del sito. Ogni sezione può avere più <strong>varianti</strong> e può essere posizionata in specifici <strong>slot</strong> del template.
                  </p>
                </div>
              </div>

              {/* Search & Filter */}
              {Object.keys(catalog.catalog.sections || {}).length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center space-x-3">
                    {/* Search Bar */}
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Cerca per ID o etichetta..."
                        className="input-sm w-full pl-10 pr-8"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Cancella ricerca"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Slot Filter */}
                    <div className="w-64">
                      <select
                        value={filterSlot}
                        onChange={(e) => setFilterSlot(e.target.value)}
                        className="input-sm w-full"
                      >
                        <option value="">Tutti gli slot</option>
                        {(() => {
                          // Get unique slots from all sections
                          const allSlots = new Set<string>();
                          Object.values(catalog.catalog.sections || {}).forEach(section => {
                            section.allowedSlots.forEach(slot => allSlots.add(slot));
                          });
                          return Array.from(allSlots).sort().map(slot => (
                            <option key={slot} value={slot}>{slot}</option>
                          ));
                        })()}
                      </select>
                    </div>

                    {/* Clear Filters */}
                    {(searchQuery || filterSlot) && (
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setFilterSlot('');
                        }}
                        className="btn-sm btn-secondary whitespace-nowrap"
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancella Filtri
                      </button>
                    )}
                  </div>

                  {/* Results count */}
                  {(searchQuery || filterSlot) && (() => {
                    const totalSections = Object.keys(catalog.catalog.sections || {}).length;
                    const filteredCount = Object.entries(catalog.catalog.sections).filter(([id, section]) => {
                      if (searchQuery) {
                        const query = searchQuery.toLowerCase();
                        const matchesId = id.toLowerCase().includes(query);
                        const matchesLabel = section.label?.toLowerCase().includes(query);
                        if (!matchesId && !matchesLabel) return false;
                      }
                      if (filterSlot && !section.allowedSlots.includes(filterSlot)) return false;
                      return true;
                    }).length;

                    return (
                      <p className="text-xs text-gray-500">
                        {filteredCount} {filteredCount === 1 ? 'sezione trovata' : 'sezioni trovate'} su {totalSections} totali
                      </p>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              <div className="pt-6">
                {/* Loading Indicator */}
                {updateMutation.isPending && (
                  <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <p className="text-sm text-blue-700">Salvataggio in corso...</p>
                  </div>
                )}

                {/* Sections Grid */}
                {Object.keys(catalog.catalog.sections || {}).length === 0 ? (
                  <div className="text-center py-12">
                    <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">Nessuna sezione ancora definita</p>
                    {isDraft && (
                      <button
                        onClick={handleCreateSection}
                        className="btn-secondary inline-flex items-center space-x-2"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Crea la tua prima sezione</span>
                      </button>
                    )}
                  </div>
                ) : (() => {
                    // Apply filters
                    const filteredSections = Object.entries(catalog.catalog.sections).filter(([id, section]) => {
                      // Search filter
                      if (searchQuery) {
                        const query = searchQuery.toLowerCase();
                        const matchesId = id.toLowerCase().includes(query);
                        const matchesLabel = section.label?.toLowerCase().includes(query);
                        if (!matchesId && !matchesLabel) return false;
                      }

                      // Slot filter
                      if (filterSlot) {
                        if (!section.allowedSlots.includes(filterSlot)) return false;
                      }

                      return true;
                    });

                    // No results after filtering
                    if (filteredSections.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <Search className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                          <p className="text-gray-500 mb-2">Nessuna sezione trovata</p>
                          <p className="text-sm text-gray-400">
                            Prova a modificare i filtri di ricerca
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredSections.map(([id, section]) => {
                      // Format variants for display
                      const variantsCount = section.variants.length;

                      return (
                        <div
                          key={id}
                          id={`section-${id}`}
                          className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 hover:shadow-md transition-all duration-200"
                        >
                          {/* Card Header */}
                          <div className="p-4 border-b border-gray-100">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-base font-semibold text-gray-900 truncate">
                                  {section.label || id}
                                </h3>
                                <p className="text-sm text-gray-500 font-mono mt-0.5">{id}</p>
                              </div>
                              {isDraft && (
                                <div className="flex items-center space-x-1 ml-2">
                                  <button
                                    onClick={() => handleEditSection(id)}
                                    className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                                    title="Modifica"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDuplicateSection(id)}
                                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                    title="Duplica"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeletingSection(id)}
                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Elimina"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                            {section.description && (
                              <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                {section.description}
                              </p>
                            )}
                          </div>

                          {/* Card Body */}
                          <div className="p-4 bg-gray-50">
                            <div className="space-y-3">
                              {/* Allowed Slots */}
                              <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                                  Slot Consentiti
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {section.allowedSlots.map(slot => (
                                    <span
                                      key={slot}
                                      className="inline-block px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded font-mono"
                                    >
                                      {slot}
                                    </span>
                                  ))}
                                </div>
                              </div>

                              {/* Variants Count */}
                              <div>
                                <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                                  Varianti
                                </p>
                                <div className="flex items-center space-x-2">
                                  <span className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded font-medium">
                                    {variantsCount} {variantsCount === 1 ? 'variante' : 'varianti'}
                                  </span>
                                  {variantsCount === 0 && (
                                    <span className="text-xs text-amber-600">⚠️ Nessuna variante</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                      </div>
                    );
                  })()}
            </div>
          </div>
        </div>
        )}
        {activeTab === 'tokens' && catalog && (
          <TokensTab
            tokenDefinitions={catalog.catalog.designTokens?.definitions as any || {}}
            tokenDefaults={catalog.catalog.designTokens?.defaults as any || {}}
            onSave={handleSaveTokens}
            readOnly={!isDraft}
          />
        )}
        {activeTab === 'templates' && catalog && (
          <TemplatesTab
            templates={catalog.catalog.templates as any || {}}
            slots={catalog.catalog.slots as any || {}}
            sections={catalog.catalog.sections as any || {}}
            onSave={handleSaveTemplates}
            readOnly={!isDraft}
          />
        )}
        {activeTab === 'themes' && catalog && (
          <ThemesTab
            themes={catalog.catalog.themes as any || []}
            onSave={handleSaveThemes}
            readOnly={!isDraft}
          />
        )}
      </div>

      {/* Export Success Toast */}
      {showExportSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3">
            <CheckCircle className="w-5 h-5" />
            <div>
              <p className="font-medium">Esportazione riuscita!</p>
              <p className="text-sm text-green-100">
                product-catalog-v{catalog.version}.json scaricato
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Save Success Toast */}
      {showSaveSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in">
          <div className="bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-3">
            <CheckCircle className="w-5 h-5" />
            <div>
              <p className="font-medium">Sezione salvata!</p>
              <p className="text-sm text-green-100">
                Le modifiche sono state salvate con successo
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Section Form Dialog */}
      {catalog && (
        <SectionFormDialog
          isOpen={showSectionDialog}
          onClose={() => {
            setShowSectionDialog(false);
            setEditingSection(undefined);
          }}
          section={editingSection}
          existingSectionIds={Object.keys(catalog.catalog.sections || {})}
          availableSlots={
            Object.values(catalog.catalog.templates || {})
              .flatMap(template => template.slots || [])
              .filter((slot, index, arr) => arr.indexOf(slot) === index) // Unique slots
          }
          onSave={handleSaveSection}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {deletingSection && catalog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Elimina Sezione
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Sei sicuro di voler eliminare la sezione{' '}
                    <span className="font-mono font-medium">{deletingSection}</span>?
                    Questa azione non può essere annullata.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3 px-6 py-4 bg-gray-50 border-t border-gray-200">
              <button
                onClick={() => setDeletingSection(null)}
                className="btn-secondary"
              >
                Annulla
              </button>
              <button
                onClick={() => handleDeleteSection(deletingSection)}
                className="btn-danger"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Publish Dialog */}
      {catalog && showPublishDialog && (
        <PublishCatalogDialog
          isOpen={showPublishDialog}
          version={catalog.version}
          onClose={() => setShowPublishDialog(false)}
        />
      )}

      {/* Warning Panel */}
      {showWarningPanel && (
        <WarningPanel
          warnings={warnings}
          onDismiss={handleDismissWarning}
          onNavigateToSection={handleNavigateToSection}
          onClose={() => setShowWarningPanel(false)}
        />
      )}

      {/* Audit Log Panel */}
      {showAuditLog && (
        <AuditLogPanel
          isOpen={showAuditLog}
          onClose={() => setShowAuditLog(false)}
          auditLogs={auditLogsData?.data.data || []}
          isLoading={isLoadingAuditLogs}
        />
      )}
    </div>
  );
}
